import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Area, Bar, CartesianGrid, ComposedChart, Line, ReferenceDot, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import styles from "./OrganisationSpendSummary.module.css";
import consumptionStyles from "../consumption/ConsumptionSummary.module.css";
import { ChartDefs, PulseDot } from "../charts/chartEffects";
import { ChartTooltip } from "../charts/ChartTooltip";
import { useChartEffectIds } from "../charts/useChartEffectIds";
import { spotlightPointerMove } from "../../utils/spotlight";
import { formatDate, formatMoney, formatRoundedMoney } from "../consumption/formatters";
import { PartialState } from "../states";
import { getSeriesColor } from "../charts/chartColors";
import { SeriesLegend, type SeriesLegendEntry } from "../charts/SeriesLegend";
import { useSeriesVisibility } from "../charts/useSeriesVisibility";
import { SectionGrid } from "../layout/SectionLayout";
import { useSectionLayout } from "../layout/sectionLayoutContext";
import type {
  BudgetReportResponse,
  GroupDailySpendSeriesResponse,
  OrganisationDailySpendResponse,
  OrganisationDailySpendSeriesResponse,
  OrganisationSpendResponse,
  OrganisationTrendResponse,
  OrganisationUnitSpendResponse,
  SpendProjectionResponse,
} from "../../api/organisationReporting";
import { isCurrentPeriod } from "../../hooks/useSelectedPeriod";
import { addMonths } from "../overview/overviewFigures";
import { BreakdownDialog } from "./BreakdownDialog";
import type { OrganisationInsight } from "./WorthALook";
import { OrganisationSection, type OrganisationSectionIcon } from "./OrganisationSection";
import {
  biggestRise,
  budgetStanding,
  budgetUnitName,
  comparisonDays,
  highestPerPerson,
  idleSeats,
  modelFamilyShare,
  modelRows,
  relabelUnclassified,
  relabelUnclassifiedDaily,
  sectionIds,
  toolRows,
  toolUsageThrough,
  unclassifiedSpend,
  unitRows,
  unitUsageThrough,
  type RankedRow,
  type UsageComparison,
} from "./organisationInsights";
import { RankedList } from "./RankedList";
import { OrganisationBudgets } from "./OrganisationBudgets";
import { Tooltip as HoverTooltip } from "../shared/Tooltip";

/** How many rows a panel lists before the rest are summed and opened in a dialog; fewer in the dense grid. */
const listedRowCount = 6;
const denseListedRowCount = 4;

/** A small bar-list glyph, on the toggle that switches a panel back to its ranking. */
function ListIcon(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
      <path d="M4 6h16M4 12h11M4 18h7" />
    </svg>
  );
}

/** A small column-graph glyph, on the toggle that switches a panel to its spend over time. */
function ColumnChartIcon(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path d="M3 3v18h18" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 17v-5" strokeLinecap="round" />
      <path d="M12 17V8" strokeLinecap="round" />
      <path d="M17 17v-9" strokeLinecap="round" />
    </svg>
  );
}

/**
 * The Organization page's content below its figures' loading (phase 14, `AIA-81`, merged with the AI overview): the hero, given the
 * "worth a look" list to show, then the Budgets section, then the trends over the year, then one panel per way of slicing the
 * month's spend (tool, model, division, department, group, and person when the viewer may see individuals). Each breakdown leads
 * with the one finding worth knowing, ranks the largest few with their change on last month, and opens the rest in a dialog; the
 * tool panel keeps the month's chart over time, and the others keep theirs a click away. Every vendor that has never synced is
 * named explicitly in development (a `PartialState`). Rendered only once the caller has a loaded response: loading, empty, error
 * and denied are the page's job.
 *
 * @param props.response - The loaded organisation-spend response.
 * @param props.dailySpend - This month's day-by-day series, or `null` when unavailable.
 * @param props.previousDailySpend - Last month's day-by-day series, for the changes; `null` when unavailable.
 * @param props.trend - The organisation trend ending at this month, for last month's total and the unused seats; `null` when unavailable.
 * @param props.permissions - What the viewer may open from the "worth a look" list.
 * @param props.budgets - This month's budget report (GH-135, R-29), or null when it is not loaded.
 * @param props.hero - Draws the hero, given the things worth a look.
 * @param props.trends - The panels over the year, placed between the Budgets section and the breakdowns.
 * @param props.children - Further sections to place in the breakdowns' grid, after them (the people panel).
 */
export function OrganisationSpendSummary({
  response: rawResponse,
  dailySpend: rawDailySpend,
  previousDailySpend: rawPreviousDailySpend = null,
  trend = null,
  permissions = { administer: false, seePeople: false },
  budgets = null,
  hero,
  trends,
  children,
}: {
  response: OrganisationSpendResponse;
  dailySpend: OrganisationDailySpendResponse | null;
  previousDailySpend?: OrganisationDailySpendResponse | null;
  trend?: OrganisationTrendResponse | null;
  permissions?: { administer: boolean; seePeople: boolean };
  budgets?: BudgetReportResponse | null;
  hero: (insights: OrganisationInsight[]) => ReactNode;
  trends?: ReactNode;
  children?: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  // The unplaced spend is named for what it is in each breakdown; the raw server label is kept for the figures that match on it.
  const unplaced = { division: t("budgets.noDivision"), department: t("budgets.noDepartment"), group: t("budgets.notInAGroup") };
  const response = relabelUnclassified(rawResponse, unplaced);
  const dailySpend = rawDailySpend ? relabelUnclassifiedDaily(rawDailySpend, unplaced) : null;
  const previousDailySpend = rawPreviousDailySpend ? relabelUnclassifiedDaily(rawPreviousDailySpend, unplaced) : null;
  const unsynced = response.vendorCompleteness.filter((vendor) => !vendor.hasEverSynced);
  const isCurrentMonth = isCurrentPeriod(response.periodStart);
  const currency = response.currency;

  // Every change compares usage over the same days of each month: the complete days so far of a running month, or whole months.
  const throughDay = comparisonDays(response.periodStart, response.generatedAt, isCurrentMonth);
  const previous = throughDay > 0 ? previousDailySpend : null;
  const compare = (current: OrganisationDailySpendSeriesResponse | null | undefined, last: OrganisationDailySpendSeriesResponse | null | undefined): UsageComparison => ({
    current: unitUsageThrough(current ?? null, throughDay),
    previous: previous && last ? unitUsageThrough(last, throughDay) : null,
  });

  const tools = toolRows(
    response.toolComparison,
    {
      current: toolUsageThrough(dailySpend?.byOrganisation ?? null, throughDay),
      previous: previous ? toolUsageThrough(previous.byOrganisation, throughDay) : null,
    },
    // Who has each tool and who used it is the "Given and used" panel's; the ranking says only what the spend needs.
    (tool) => (tool.seatCostIsEstimate ? t("organisation.ranked.seatEstimate") : null),
  );
  const models = modelRows(dailySpend?.byModel ?? null, compare(dailySpend?.byModel, previous?.byModel));
  // A unit's page names its people, so only a viewer who may read named people opens it from here.
  const divisions = unitRows(response.byDivision, compare(dailySpend?.byDivision, previous?.byDivision), permissions.seePeople);
  const departments = unitRows(response.byDepartment, compare(dailySpend?.byDepartment, previous?.byDepartment), permissions.seePeople);
  const groups = unitRows(response.byGroup, compare(dailySpend?.byGroup, previous?.byGroup), permissions.seePeople);

  const previousMonth = addMonths(response.periodStart, -1);
  const trendMonth = trend?.months.find((month) => month.periodStart === response.periodStart) ?? null;
  const comparedOver = throughDay > 0 && previous ? comparisonText(t, response.periodStart, previousMonth, throughDay, isCurrentMonth) : null;

  // A link back to a section of this page (a person's page returns to the people) scrolls to it once the page has its figures.
  const location = useLocation();
  useEffect(() => {
    const id = location.hash.slice(1);
    if (id !== "" && Object.values(sectionIds).includes(id as (typeof sectionIds)[keyof typeof sectionIds])) {
      document.getElementById(id)?.scrollIntoView({ block: "start" });
    }
  }, [location.hash]);

  const insights = buildInsights({
    t,
    currency,
    response: rawResponse,
    departments,
    trend,
    trendMonth,
    permissions,
    budgets,
  });

  return (
    <div onPointerMove={spotlightPointerMove}>
      {/* A never-synced vendor is a development-time fact, not something to show users: the notice is dev-only. */}
      {import.meta.env.DEV && unsynced.map((vendor) => (
        <PartialState key={vendor.vendorId} vendorName={vendor.vendorDisplayName} description={vendor.note ?? undefined} />
      ))}

      {hero(insights)}
      {budgets && <OrganisationBudgets report={budgets} />}
      {trends}

      <SectionGrid>
        <BreakdownPanel
          id={sectionIds.tool}
          icon="tool"
          title={t("organisation.spendByTool")}
          subtitle={t("organisation.sections.tool")}
          headline={toolHeadline(t, tools)}
          rows={tools}
          currency={currency}
          unitName={t("organisation.units.tools")}
          labelHeader={t("organisation.table.tool")}
          fileName="organisation-by-tool.csv"
          comparedOver={comparedOver}
          chart={
            <ProviderSpendOverTimeChart
              response={response}
              dailySpend={dailySpend?.byOrganisation ?? null}
              projection={dailySpend?.projection ?? null}
              isCurrentMonth={isCurrentMonth}
            />
          }
        />

        <BreakdownPanel
          id={sectionIds.model}
          icon="model"
          title={t("organisation.byModel")}
          subtitle={t("organisation.sections.model")}
          headline={modelHeadline(t, models)}
          rows={models}
          currency={currency}
          unitName={t("organisation.units.models")}
          labelHeader={t("organisation.table.model")}
          fileName="organisation-by-model.csv"
          comparedOver={comparedOver}
          empty={dailySpend !== null ? t("organisation.noModelSpend") : null}
          chart={<UnitSpendOverTimeChart units={unitsOf(models, currency)} dailySpend={dailySpend?.byModel ?? null} ariaLabelKey="organisation.chart.modelSpendOverTime" />}
        />

        <BreakdownPanel
          id={sectionIds.division}
          icon="division"
          title={t("organisation.byDivision")}
          subtitle={t("organisation.sections.division")}
          headline={unitHeadline(t, divisions, currency)}
          rows={divisions}
          currency={currency}
          unitName={t("organisation.units.divisions")}
          labelHeader={t("organisation.table.division")}
          fileName="organisation-by-division.csv"
          comparedOver={comparedOver}
          empty={t("organisation.noAttributedSpend")}
          chart={<UnitSpendOverTimeChart units={response.byDivision} dailySpend={dailySpend?.byDivision ?? null} />}
        />
        <BreakdownPanel
          id={sectionIds.department}
          icon="department"
          title={t("organisation.byDepartment")}
          subtitle={t("organisation.sections.department")}
          headline={unitHeadline(t, departments, currency)}
          rows={departments}
          currency={currency}
          unitName={t("organisation.units.departments")}
          labelHeader={t("organisation.table.department")}
          fileName="organisation-by-department.csv"
          comparedOver={comparedOver}
          empty={t("organisation.noAttributedSpend")}
          chart={<UnitSpendOverTimeChart units={response.byDepartment} dailySpend={dailySpend?.byDepartment ?? null} />}
        />
        <BreakdownPanel
          id={sectionIds.group}
          icon="group"
          title={t("organisation.byGroup")}
          subtitle={t("organisation.sections.group")}
          headline={groupHeadline(t, groups)}
          rows={groups}
          currency={currency}
          unitName={t("organisation.units.groups")}
          labelHeader={t("organisation.table.group")}
          fileName="organisation-by-group.csv"
          comparedOver={comparedOver}
          empty={t("organisation.noAttributedSpend")}
          chart={<UnitSpendOverTimeChart units={response.byGroup} dailySpend={dailySpend?.byGroup ?? null} />}
        />
        {children}
      </SectionGrid>
    </div>
  );
}

type Translate = ReturnType<typeof useTranslation>["t"];

/**
 * One panel of the Organisation page: its finding, its largest rows ranked, the rest summed with a button that opens them all in
 * a dialog, and a chart over time, either always above the ranking (the tool panel) or behind the switch in its header.
 *
 * @param props.id - The section's id, which a "worth a look" item can point at.
 * @param props.icon - Which icon heads the card.
 * @param props.title - The panel's title.
 * @param props.subtitle - One line saying what it shows.
 * @param props.headline - The one finding it leads with, or null.
 * @param props.rows - Every row, largest first.
 * @param props.currency - The currency the spend is in.
 * @param props.unitName - What one row is, plural (`divisions`).
 * @param props.labelHeader - The CSV heading for a row's name.
 * @param props.fileName - The CSV file's name.
 * @param props.comparedOver - Which days the changes compare, or null when there are none.
 * @param props.empty - What to say when there are no rows, or null to show nothing (still loading).
 * @param props.chart - The panel's chart over time.
 * @param props.children - Anything to show beneath the ranking.
 */
export function BreakdownPanel({
  id,
  icon,
  title,
  subtitle,
  headline,
  rows,
  currency,
  unitName,
  labelHeader,
  fileName,
  comparedOver,
  empty = null,
  chart,
  children,
}: {
  id: string;
  icon: OrganisationSectionIcon;
  title: string;
  subtitle: string;
  headline: string | null;
  rows: RankedRow[];
  currency: string;
  unitName: string;
  labelHeader: string;
  fileName: string;
  comparedOver: string | null;
  empty?: string | null;
  chart?: ReactNode;
  children?: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [view, setView] = useState<"list" | "chart">("list");
  const [showAll, setShowAll] = useState(false);
  const dense = useSectionLayout().mode === "dense";
  const showChart = chart !== undefined && view === "chart";
  const showList = view === "list";
  const toggleLabel = view === "list" ? t("organisation.chart.toggleTimeSeries") : t("organisation.chart.toggleList");
  const hasChange = rows.some((row) => row.change !== null);

  return (
    <OrganisationSection
      icon={icon}
      headingId={`${id}-heading`}
      sectionId={id}
      title={title}
      subtitle={subtitle}
      action={
        chart !== undefined && rows.length > 0 ? (
          <HoverTooltip label={toggleLabel} align="end">
            <button
              type="button"
              className={styles.chartToggleButton}
              aria-label={toggleLabel}
              onClick={() => setView(view === "list" ? "chart" : "list")}
            >
              {view === "list" ? <ColumnChartIcon /> : <ListIcon />}
            </button>
          </HoverTooltip>
        ) : undefined
      }
    >
      {rows.length === 0 ? (
        empty && <p className={styles.note}>{empty}</p>
      ) : (
        <>
          {headline && <p className={styles.headline}>{headline}</p>}
          {showChart && <div className={styles.chartPanel}>{chart}</div>}
          {showList && (
            <div>
              <RankedList
                rows={rows}
                currency={currency}
                label={title}
                limit={dense ? denseListedRowCount : listedRowCount}
                onShowAll={() => setShowAll(true)}
                unitName={unitName}
              />
              {hasChange && comparedOver && <p className={styles.compareNote}>{comparedOver}</p>}
            </div>
          )}
          {children}
        </>
      )}
      {showAll && (
        <BreakdownDialog title={title} rows={rows} currency={currency} labelHeader={labelHeader} fileName={fileName} onClose={() => setShowAll(false)} />
      )}
    </OrganisationSection>
  );
}

/**
 * Which days the changes compare: the same complete days of this month and last while a month runs, or the two whole months.
 *
 * @returns The note.
 */
function comparisonText(t: Translate, periodStart: string, previousMonth: string, throughDay: number, isCurrentMonth: boolean): string {
  const monthName = (start: string): string => new Date(`${start}T00:00:00Z`).toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
  return isCurrentMonth
    ? t("organisation.ranked.comparedDays", { days: throughDay, month: monthName(periodStart), previous: monthName(previousMonth) })
    : t("organisation.ranked.comparedMonths", { month: monthName(periodStart), previous: monthName(previousMonth) });
}

/** The rows as units, for the charts over time, which draw units. */
function unitsOf(rows: RankedRow[], currency: string): OrganisationUnitSpendResponse[] {
  return rows.map((row) => ({ unitLabel: row.label, totalSpend: row.spend, currency, wasFullyConverted: true, personCount: row.people ?? 0 }));
}

/** A whole percentage, as text. */
function whole(percent: number): string {
  return String(Math.round(percent));
}

/** The change from which a rise is said as a multiple ("32 times") rather than a percentage ("up 3093%"), which reads as noise. */
const multipleFrom = 2;

/**
 * A rise in words, as a percentage or, from three times last month's usage, as a multiple.
 *
 * @param key - The translation key; its `Times` form is used for a multiple.
 * @param name - What rose.
 * @param change - The rise, as a fraction.
 * @returns The sentence.
 */
function riseText(t: Translate, key: string, name: string, change: number): string {
  return change >= multipleFrom ? t(`${key}Times`, { name, times: Math.round(change + 1) }) : t(key, { name, percent: whole(change * 100) });
}

/**
 * The tool panel's finding: the tool most of the spend goes on, and the tool whose use rose most. Take-up is the "Given and used"
 * panel's finding, so it is not repeated here.
 *
 * @returns The sentence, or null when there are no tools.
 */
function toolHeadline(t: Translate, rows: RankedRow[]): string | null {
  const top = rows[0];
  if (!top) {
    return null;
  }

  const lead = t("organisation.headline.share", { name: top.label, percent: whole(top.sharePercent) });
  const rise = biggestRise(rows);
  return rise ? `${lead} ${riseText(t, "organisation.headline.rise", rise.label, rise.change ?? 0)}` : lead;
}

/**
 * The model panel's finding: how much of the spend goes on the Opus models (the costliest), or failing that on the top model, and
 * the model whose use rose most.
 *
 * @returns The sentence, or null when there are no models.
 */
function modelHeadline(t: Translate, rows: RankedRow[]): string | null {
  const top = rows[0];
  if (!top) {
    return null;
  }

  const opus = modelFamilyShare(rows, "opus");
  const lead = opus
    ? t("organisation.headline.modelFamily", { family: "Opus", percent: whole(opus.sharePercent) })
    : t("organisation.headline.share", { name: top.label, percent: whole(top.sharePercent) });
  const rise = biggestRise(rows);
  return rise ? `${lead} ${riseText(t, "organisation.headline.rise", rise.label, rise.change ?? 0)}` : lead;
}

/**
 * A division's or department's finding: the unit most of the spend comes from, and the one that spends most per person.
 *
 * @returns The sentence, or null when there are no units.
 */
function unitHeadline(t: Translate, rows: RankedRow[], currency: string): string | null {
  const top = rows[0];
  if (!top) {
    return null;
  }

  const heaviest = highestPerPerson(rows);
  if (heaviest?.key === top.key && heaviest.perPerson !== null) {
    return t("organisation.headline.shareAndPerPerson", { name: top.label, percent: whole(top.sharePercent), amount: formatRoundedMoney(heaviest.perPerson, currency) });
  }

  const lead = t("organisation.headline.share", { name: top.label, percent: whole(top.sharePercent) });
  return heaviest && heaviest.perPerson !== null
    ? `${lead} ${t("organisation.headline.perPerson", { name: heaviest.label, amount: formatRoundedMoney(heaviest.perPerson, currency) })}`
    : lead;
}

/**
 * The group panel's finding: the group whose use rose most on last month, or failing that the group most of the spend comes from.
 *
 * @returns The sentence, or null when there are no groups.
 */
function groupHeadline(t: Translate, rows: RankedRow[]): string | null {
  const rise = biggestRise(rows);
  if (rise) {
    return riseText(t, "organisation.headline.riseLead", rise.label, rise.change ?? 0);
  }

  const top = rows[0];
  return top ? t("organisation.headline.share", { name: top.label, percent: whole(top.sharePercent) }) : null;
}

/**
 * The hero's "worth a look" list: units over their budgets, spend no division holds, seats paid for and not used, and the
 * department whose use rose most. Each opens where it can be acted on, where the viewer may open it. How concentrated the spend
 * is stays a figure beside the hero, not an item here.
 *
 * @returns The items, most important first.
 */
function buildInsights({
  t,
  currency,
  response,
  departments,
  trend,
  trendMonth,
  permissions,
  budgets,
}: {
  t: Translate;
  currency: string;
  response: OrganisationSpendResponse;
  departments: RankedRow[];
  trend: OrganisationTrendResponse | null;
  trendMonth: OrganisationTrendResponse["months"][number] | null;
  permissions: { administer: boolean; seePeople: boolean };
  budgets: BudgetReportResponse | null;
}): OrganisationInsight[] {
  const insights: OrganisationInsight[] = [];

  const standing = budgets ? budgetStanding(budgets) : null;
  if (budgets && standing?.furthestOver) {
    insights.push({
      key: "over-budget",
      title: t("organisation.insight.overBudget.title", { count: standing.overCount }),
      // The overspend is the item's figure, so its note says what it is measured against rather than repeating it.
      detail: t("organisation.insight.overBudget.detail", {
        count: standing.overCount,
        name: budgetUnitName(budgets, standing.furthestOver.unit),
        spent: formatRoundedMoney(standing.furthestOver.unit.countedSpendUsd, budgets.currency),
        budget: formatRoundedMoney(standing.furthestOver.unit.budgetUsd ?? 0, budgets.currency),
      }),
      figure: formatRoundedMoney(standing.furthestOver.overBy, budgets.currency),
      target: `#${sectionIds.budget}`,
    });
  }

  const unclassified = unclassifiedSpend(response);
  if (unclassified) {
    insights.push({
      key: "unclassified",
      title: t("organisation.insight.unclassified.title", { count: unclassified.people }),
      detail: t("organisation.insight.unclassified.detail", { percent: whole(unclassified.sharePercent) }),
      figure: formatRoundedMoney(unclassified.spend, currency),
      target: permissions.administer ? "/admin/users" : undefined,
    });
  }

  if (trend && trendMonth) {
    for (const idle of idleSeats(trend, trendMonth).slice(0, 2)) {
      insights.push({
        key: `idle-${idle.vendorId}`,
        title: t("organisation.insight.idle.title", { count: idle.people, tool: idle.displayName }),
        detail: t("organisation.insight.idle.detail"),
        figure: formatRoundedMoney(idle.spend, currency),
        // Without the people's names, the "Given and used" panel is where the seats are explained.
        target: permissions.seePeople ? "/admin/system/accounts" : `#${sectionIds.seats}`,
      });
    }
  }

  const rise = biggestRise(departments);
  if (rise) {
    insights.push({
      key: "rise",
      title: t("organisation.insight.rise.title", { name: rise.label }),
      detail: riseText(t, "organisation.insight.rise.detail", rise.label, rise.change ?? 0),
      figure: rise.spend !== null ? formatRoundedMoney(rise.spend, currency) : "–",
      target: `#${sectionIds.department}`,
    });
  }

  return insights;
}

/** One day's chart point: a spend value per vendor, the day's overall total, the running total up to and including that day, and (from today onward) the projected running total to period end. */
type OrganisationChartPoint = Record<string, number | string | null> & {
  name: string;
  total: number;
  cumulative: number | null;
  projected: number | null;
};

/**
 * The provider-by-day column chart with its running total and projection; the body of the top chart,
 * shown when the toggle is on the time-series view.
 */
function ProviderSpendOverTimeChart({
  response,
  dailySpend,
  projection,
  isCurrentMonth,
}: {
  response: OrganisationSpendResponse;
  dailySpend: GroupDailySpendSeriesResponse | null;
  projection: SpendProjectionResponse | null;
  isCurrentMonth: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { isHidden, toggle } = useSeriesVisibility();
  const effects = useChartEffectIds();

  if (!dailySpend || !Array.isArray(dailySpend.days) || dailySpend.days.length === 0) {
    return (
      <div className={styles.chartFrame}>
        <div
          className={consumptionStyles.chartContainer}
          style={{ minHeight: 260 }}
          role="img"
          aria-label={t("organisation.chart.totalOverTime")}
        />
      </div>
    );
  }

  // Every provider with data becomes its own stacked bar, in the same order as
  // `response.toolComparison` (and therefore the "By tool" table/cards below) rather than the
  // order providers happen to first appear across `dailySpend.days` - that order is unstable (it
  // depends on which day's data happens to list a vendor first) and previously caused the chart's
  // series colors/legend order to drift out of sync with both the palette convention (pink, blue,
  // yellow, ...) and the tool ordering used elsewhere on the page.
  const vendorIdsWithData = new Set(dailySpend.days.flatMap((day) => day.vendors.map((vendor) => vendor.vendorId)));
  const vendorIds = response.toolComparison.map((tool) => tool.vendorId).filter((vendorId) => vendorIdsWithData.has(vendorId));
  const vendorNamesById = new Map(response.toolComparison.map((tool) => [tool.vendorId, tool.vendorDisplayName]));

  const periodEnd = new Date(`${response.periodEnd}T00:00:00Z`);
  const generatedAt = new Date(response.generatedAt);
  const currentPoint = generatedAt > periodEnd ? periodEnd : generatedAt;
  const currentDateKey = currentPoint.toISOString().slice(0, 10);
  const projectedByDate = new Map((projection?.points ?? []).map((p) => [p.date, p.cumulativeAmount]));

  // The days are usage alone, so the running total starts at the period's seat fees.
  let runningTotal = dailySpend.seatFees ?? 0;
  const chartData: OrganisationChartPoint[] = dailySpend.days.map((day) => {
    const isTodayOrEarlier = day.date <= currentDateKey;
    if (isTodayOrEarlier) {
      runningTotal += day.totalAmount;
    }

    const point: OrganisationChartPoint = {
      name: formatDate(day.date),
      total: day.totalAmount,
      cumulative: isTodayOrEarlier ? runningTotal : null,
      // The server's projection starts at today, so the projected line picks up right where the
      // cumulative-total line leaves off.
      projected: projectedByDate.get(day.date) ?? null,
    };
    for (const vendorId of vendorIds) {
      point[vendorId] = day.vendors.find((vendor) => vendor.vendorId === vendorId)?.amount ?? 0;
    }

    return point;
  });

  // Recharts does not reliably keep a stacked bar chart's auto-generated legend in the same
  // order its series were declared in (it can reorder items relative to the visual stack), so an
  // explicit legend is rendered via {@link SeriesLegend} instead - this is what actually
  // guarantees the legend lines up with both the palette order (pink, blue, yellow, ...) and the
  // "By tool" table below.
  const legendEntries: SeriesLegendEntry[] = [
    ...vendorIds.map((vendorId, index) => ({
      key: vendorId,
      label: vendorNamesById.get(vendorId) ?? vendorId,
      color: getSeriesColor(index),
    })),
    {
      key: "cumulative",
      label: t("organisation.chart.total"),
      color: getSeriesColor(vendorIds.length),
      shape: "line" as const,
    },
    ...(isCurrentMonth ? [{
      key: "projected",
      label: t("organisation.chart.projected"),
      color: getSeriesColor(vendorIds.length + 1),
      shape: "line" as const,
    }] : []),
  ];
  const tooltipColors = new Map(legendEntries.map((entry) => [entry.key, entry.color]));
  // Today's running total, pulsed on a month that is still running.
  const todayPoint = isCurrentMonth ? [...chartData].reverse().find((point) => point.cumulative !== null) : undefined;

  return (
    <div className={styles.chartFrame}>
      <div
        className={consumptionStyles.chartContainer}
        role="img"
        aria-label={t("organisation.chart.totalOverTime")}
      >
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 24, right: 16, bottom: 20, left: 8 }}>
            <ChartDefs
              ids={effects}
              gradients={[
                ...vendorIds.map((vendorId, index) => ({ key: vendorId, color: getSeriesColor(index), top: 1, bottom: 0.55 })),
                { key: "cumulative", color: getSeriesColor(vendorIds.length), top: 0.28, bottom: 0 },
              ]}
            />
            <CartesianGrid vertical={false} stroke="color-mix(in srgb, var(--color-border-default) 70%, transparent)" strokeDasharray="4 4" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }} />
            <YAxis
              yAxisId="daily"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }}
              tickFormatter={(value: number) => `$${String(Math.round(value))}`}
              width={48}
            />
            <YAxis
              yAxisId="total"
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }}
              tickFormatter={(value: number) => `$${String(Math.round(value))}`}
              width={48}
            />
            <Tooltip
              content={<ChartTooltip colors={tooltipColors} formatLabel={(label) => label} formatValue={(value) => formatMoney(value, response.currency)} />}
              cursor={{ fill: "color-mix(in srgb, var(--color-chart-blue) 10%, transparent)" }}
            />
            {vendorIds.map((vendorId, index) => (
              <Bar
                key={vendorId}
                yAxisId="daily"
                stackId="vendors"
                dataKey={vendorId}
                name={vendorNamesById.get(vendorId) ?? vendorId}
                fill={`url(#${effects.gradient(vendorId)})`}
                // Only the top of the stack is rounded; a rounded segment inside it would leave a notch.
                radius={index === vendorIds.length - 1 ? [3, 3, 0, 0] : 0}
                hide={isHidden(vendorId)}
              />
            ))}
            <Area
              yAxisId="total"
              type="monotone"
              dataKey="cumulative"
              name={t("organisation.chart.total")}
              stroke={getSeriesColor(vendorIds.length)}
              strokeWidth={2.5}
              fill={`url(#${effects.gradient("cumulative")})`}
              filter={`url(#${effects.glow})`}
              dot={false}
              connectNulls
              hide={isHidden("cumulative")}
            />
            {isCurrentMonth && <Line
              yAxisId="total"
              type="monotone"
              dataKey="projected"
              name={t("organisation.chart.projected")}
              stroke={getSeriesColor(vendorIds.length + 1)}
              strokeWidth={2.5}
              strokeDasharray="6 4"
              strokeLinecap="round"
              filter={`url(#${effects.glow})`}
              dot={false}
              connectNulls
              hide={isHidden("projected")}
            />}
            {todayPoint?.cumulative !== null && todayPoint?.cumulative !== undefined && !isHidden("cumulative") && (
              <ReferenceDot yAxisId="total" x={todayPoint.name} y={todayPoint.cumulative} fill={getSeriesColor(vendorIds.length)} shape={PulseDot} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <SeriesLegend entries={legendEntries} isHidden={isHidden} onToggle={toggle} />
    </div>
  );
}

/** The most series a chart draws; past this, the smallest are drawn together as "Other", so the chart and its legend stay readable. */
const maxChartSeries = 8;

/** The key of the "Other" series, which no unit's label can be. */
const otherKey = "\u0000other";

/** The colour of the "Other" series: neutral, so it never reads as one more unit. */
const otherColour = "var(--color-text-secondary)";

/**
 * The units a chart draws by name: all of them when they fit in {@link maxChartSeries}, otherwise the largest, leaving room
 * for "Other". The rest are summed into "Other" by whoever draws them. The tables beneath still list every unit.
 *
 * @param labels - The units' labels, in the order the section lists them.
 * @param totalOf - Each unit's total for the period, to find the largest.
 * @returns The labels drawn by name, in the section's order, and how many are drawn as "Other".
 */
function chartSeries(labels: string[], totalOf: (label: string) => number): { named: string[]; otherCount: number } {
  if (labels.length <= maxChartSeries) {
    return { named: labels, otherCount: 0 };
  }

  const largest = new Set([...labels].sort((a, b) => totalOf(b) - totalOf(a)).slice(0, maxChartSeries - 1));
  return { named: labels.filter((label) => largest.has(label)), otherCount: labels.length - largest.size };
}

/** One day's chart point: a spend value per unit plus the grand total across every unit that day. */
type UnitChartPoint = Record<string, number | string> & { name: string; total: number };

/**
 * A time-series alternative to {@link UnitSpendChart}: one line per unit in the section plus a
 * grand-total line, day by day for the current period - the same style as the "My Groups" chart's
 * per-vendor lines, but keyed by organisational unit instead of vendor.
 */
function UnitSpendOverTimeChart({
  units,
  dailySpend,
  ariaLabelKey = "organisation.chart.spendOverTime",
}: {
  units: OrganisationUnitSpendResponse[];
  dailySpend: OrganisationDailySpendSeriesResponse | null;
  ariaLabelKey?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { isHidden, toggle } = useSeriesVisibility();
  const effects = useChartEffectIds();

  // Always render the panel shell - including its chartContainer, which reserves the chart's
  // rendered height - even before dailySpend has arrived, so the page doesn't collapse and then
  // shudder once the toggle is used and the data resolves.
  if (!dailySpend || !Array.isArray(dailySpend.days) || dailySpend.days.length === 0) {
    return (
      <div className={styles.chartFrame}>
        <div
          className={consumptionStyles.chartContainer}
          style={{ minHeight: 260 }}
          role="img"
          aria-label={t(ariaLabelKey)}
        />
      </div>
    );
  }

  const currency = dailySpend.days.find((day) => day.units.length > 0)?.currency ?? "USD";
  // Same order as `units` (and therefore the pie chart and table below), not the order units
  // happen to first appear across `dailySpend.days` - that order is unstable and previously
  // caused this chart's series colors/legend order to drift out of sync with the rest of the
  // section.
  const unitIdsWithData = new Set(dailySpend.days.flatMap((day) => day.units.map((unit) => unit.unit)));
  const totals = new Map(units.map((unit) => [unit.unitLabel, unit.totalSpend ?? 0]));
  const { named: unitLabels, otherCount } = chartSeries(
    units.map((unit) => unit.unitLabel).filter((unitLabel) => unitIdsWithData.has(unitLabel)),
    (label) => totals.get(label) ?? 0,
  );
  const namedSet = new Set(unitLabels);

  const chartData: UnitChartPoint[] = dailySpend.days.map((day) => {
    const point: UnitChartPoint = { name: formatDate(day.date), total: day.totalAmount };
    for (const unit of unitLabels) {
      point[unit] = day.units.find((u) => u.unit === unit)?.amount ?? 0;
    }
    if (otherCount > 0) {
      point[otherKey] = day.units.filter((u) => !namedSet.has(u.unit)).reduce((sum, u) => sum + u.amount, 0);
    }

    return point;
  });

  // See the equivalent comment on OrganisationSpendOverTimeChart - an explicit legend, rendered
  // via {@link SeriesLegend}, is required to keep the legend order in sync with the stacked bars'
  // declared order.
  const otherLabel = t("organisation.chart.other", { count: otherCount });
  const legendEntries: SeriesLegendEntry[] = [
    ...unitLabels.map((unit, index) => ({ key: unit, label: unit, color: getSeriesColor(index) })),
    ...(otherCount > 0 ? [{ key: otherKey, label: otherLabel, color: otherColour }] : []),
  ];
  const tooltipColors = new Map(legendEntries.map((entry) => [entry.key, entry.color]));
  const lastSeries = otherCount > 0 ? otherKey : unitLabels[unitLabels.length - 1];

  return (
    <div className={styles.chartFrame}>
      <div
        className={consumptionStyles.chartContainer}
        style={{ minHeight: 260 }}
        role="img"
        aria-label={t(ariaLabelKey)}
      >
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 24, right: 16, bottom: 20, left: 8 }}>
            <ChartDefs ids={effects} gradients={legendEntries.map((entry) => ({ key: entry.key, color: entry.color, top: 1, bottom: 0.55 }))} />
            <CartesianGrid vertical={false} stroke="color-mix(in srgb, var(--color-border-default) 70%, transparent)" strokeDasharray="4 4" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }} />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }}
              tickFormatter={(value: number) => `$${String(Math.round(value))}`}
              width={48}
            />
            <Tooltip
              content={<ChartTooltip colors={tooltipColors} formatLabel={(label) => label} formatValue={(value) => formatMoney(value, currency)} />}
              cursor={{ fill: "color-mix(in srgb, var(--color-chart-blue) 10%, transparent)" }}
            />
            {unitLabels.map((unit) => (
              <Bar
                key={unit}
                stackId="units"
                dataKey={unit}
                name={unit}
                fill={`url(#${effects.gradient(unit)})`}
                radius={unit === lastSeries ? [3, 3, 0, 0] : 0}
                hide={isHidden(unit)}
              />
            ))}
            {otherCount > 0 && (
              <Bar stackId="units" dataKey={otherKey} name={otherLabel} fill={`url(#${effects.gradient(otherKey)})`} radius={[3, 3, 0, 0]} hide={isHidden(otherKey)} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <SeriesLegend entries={legendEntries} isHidden={isHidden} onToggle={toggle} />
    </div>
  );
}
